/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useDispatch, useSelector } from 'react-redux';
import { memo, useCallback, useMemo } from 'react';
import {
  BluContainerBlock,
  BludataRightSideSectionMenu,
  BluSyncDatePicker,
} from '@bludata/components';
import { AppDispatch, RootState } from 'packages/blu-booking/src/store';
import {
  DefaultCalendarRemoveStudio,
  DefaultCalendarResetSelected,
  DefaultCalendarSelectPointOfSale,
  DefaultCalendarSelectStudio,
  DefaultCalendarSelectZoomValue,
  DefaultCalendarSetSelectedDate,
  DefaultCalendarSetService,
  DefaultCalendarSetUser,
} from '../store';
import {
  ServiceFilter,
  StudiFilter,
  PointOfSaleFilter,
  CalendarDaySelection,
  UsersFilter,
} from '../../../UI/AppointmentFilters';
import { StudioInterface } from 'packages/blu-booking/src/interfaces/Studio';
import { RapidFunctions } from '../../../UI/AppointmentFilters/RapidFunctions';
import { Zoom } from '../../../UI/AppointmentFilters/Zoom';
import { ZoomItem } from '../../../interfaces/Zoom';
import { setRefetch } from '../../../store/Appointment';
import { AppointmentCompleteInterface } from 'packages/blu-booking/src/interfaces/AppointmentComplete';

export const Filters = memo(function Drawer(): JSX.Element {
  const {
    selectedServices,
    selectedPointOfSale,
    selectedStudi,
    selectedDate,
    selectedUsers,
    view,
    zoomValue,
  } = useSelector((state: RootState) => state.bluBookingDefaultCalendar);

  const { pointOfSales, studi } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );

  const dispatch = useDispatch<AppDispatch>();

  const getRange = useCallback((): [number, dayjs.ManipulateType] => {
    switch (view) {
      case 'Month':
        return [1, 'months'];
      case 'Week':
        return [1, 'week'];
      case '3Days':
        return [3, 'days'];
      default:
        return [1, 'days'];
    }
  }, [view]);

  const CalendarSelection = useMemo(() => {
    return (
      <CalendarDaySelection
        move={getRange()}
        selectedDate={selectedDate}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setSelectedDate={(value: any) =>
          dispatch(DefaultCalendarSetSelectedDate(value))
        }
      />
    );
  }, [getRange, selectedDate, dispatch]);

  const handlePosition = useCallback(
    (appointment: AppointmentCompleteInterface) => {
      const d = dayjs(selectedDate).format('DD/MM/YYYY');
      const appointmentDate = dayjs(appointment?.startTime);

      if (
        appointment?.pointOfSale &&
        appointment?.pointOfSale?.id &&
        pointOfSales &&
        appointment?.pointOfSale?.id in pointOfSales
      )
        dispatch(
          DefaultCalendarSelectPointOfSale(
            pointOfSales[appointment?.pointOfSale?.id]
          )
        );
      if (selectedStudi.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedStudi'));

      if (selectedServices.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedServices'));

      if (selectedUsers.length > 0)
        dispatch(DefaultCalendarResetSelected('selectedUsers'));

      if (d !== appointmentDate.format('DD/MM/YYYY'))
        dispatch(DefaultCalendarSetSelectedDate(appointmentDate.toDate()));
      else dispatch(setRefetch());
    },
    [
      dispatch,
      pointOfSales,
      selectedDate,
      selectedServices.length,
      selectedStudi.length,
      selectedUsers.length,
    ]
  );

  return (
    <BludataRightSideSectionMenu
      sx={{ mt: 0.5, mr: 0.5, mb: 0.5, pr: 0, height: '100%' }}
    >
      <Stack
        flexDirection="column"
        flex={1}
        sx={{ width: 250, height: '100%', p: '8px', pr: '3px' }}
      >
        {CalendarSelection}
        <BluSyncDatePicker
          date={selectedDate}
          onChange={(event: any) => {
            dispatch(DefaultCalendarSetSelectedDate(event.target.value));
          }}
        />
        <Stack
          flexDirection="column"
          flex="1"
          sx={{ overflowX: 'hidden' }}
          spacing={1}
        >
          <PointOfSaleFilter
            selectedPointOfSales={selectedPointOfSale}
            addPointOfSale={(value: any) =>
              dispatch(DefaultCalendarSelectPointOfSale(value))
            }
          />
          <ServiceFilter
            selectedService={selectedServices}
            pointOfSaleId={selectedPointOfSale?.id}
            addService={(value: any) =>
              dispatch(DefaultCalendarSetService(value))
            }
            resetService={() =>
              dispatch(DefaultCalendarResetSelected('selectedServices'))
            }
          />

          {selectedPointOfSale && (
            <StudiFilter
              selectedStudio={selectedStudi}
              studi={
                Object.values(studi).filter(
                  (v) =>
                    (v as StudioInterface).pointOfSaleId ===
                    selectedPointOfSale.id
                ) as StudioInterface[]
              }
              removeStudio={(value: any) =>
                dispatch(DefaultCalendarRemoveStudio(value))
              }
              addStudio={(value: any) =>
                dispatch(DefaultCalendarSelectStudio(value))
              }
              resetStudio={(value: any) =>
                dispatch(DefaultCalendarResetSelected(value))
              }
            />
          )}
          <UsersFilter
            selectedUsers={selectedUsers}
            pointOfSaleId={selectedPointOfSale?.id}
            addUsers={(value: any) => dispatch(DefaultCalendarSetUser(value))}
            resetUsers={() =>
              dispatch(DefaultCalendarResetSelected('selectedUsers'))
            }
          />
          {selectedPointOfSale && (
            <RapidFunctions
              pointOfSaleId={selectedPointOfSale?.pointOfSaleId}
              handlePosition={handlePosition}
            />
          )}
        </Stack>
        <Zoom
          value={zoomValue}
          change={(value: ZoomItem) => DefaultCalendarSelectZoomValue(value)}
        />
      </Stack>
    </BludataRightSideSectionMenu>
  );
});
